import type { CSSProperties, ReactNode } from 'react';
import type { Tone } from './ui';
import styles from './Gauge.module.css';

/**
 * Ring gauge for at-a-glance ratios. Past 100% the arc clamps at a full turn
 * while the centre keeps the real number; the owning row spells "Over cap"
 * out, since a second line inside a small ring collides with the stroke.
 */
export const Gauge = ({
  value,
  tone = 'accent',
  size = 72,
  thickness = 6,
  label,
  caption,
  ariaLabel,
}: {
  /** Share consumed, 0..100+ (unclamped for the label). */
  value: number;
  tone?: Tone;
  /** Outer diameter in px. */
  size?: number;
  /** Ring thickness in px. */
  thickness?: number;
  /** Centre content; defaults to the rounded percentage. */
  label?: ReactNode;
  /** Small caption under the value, inside the ring. */
  caption?: ReactNode;
  ariaLabel?: string;
}) => {
  const clamped = Math.max(0, Math.min(100, value ?? 0));
  const r = (size - thickness) / 2;
  const c = 2 * Math.PI * r;
  const dash = (clamped / 100) * c;
  const isOverCap = value > 100;

  return (
    <div
      className={styles.gauge}
      data-tone={tone}
      data-over={isOverCap}
      data-small={size <= 64}
      style={{ '--gauge-size-default': `${size}px` } as CSSProperties}
      role="meter"
      aria-valuenow={clamped}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={ariaLabel}
      aria-valuetext={
        isOverCap ? `${Math.round(value)}% — over cap` : undefined
      }
    >
      <svg
        viewBox={`0 0 ${size} ${size}`}
        width={size}
        height={size}
        className={styles.svg}
        aria-hidden
      >
        <circle
          className={styles.track}
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          strokeWidth={thickness}
        />
        <circle
          className={styles.arc}
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          strokeWidth={thickness}
          strokeLinecap="round"
          strokeDasharray={`${dash} ${c - dash}`}
        />
      </svg>
      <div className={styles.centre}>
        <span className={styles.value}>{label ?? `${Math.round(value)}%`}</span>
        {caption && <span className={styles.caption}>{caption}</span>}
      </div>
    </div>
  );
};
